import { debugLog } from './debug';
interface PerformanceMetrics {
  renderTime: number;
  interactionTime: number;
  memoryUsage: number;
  domNodes: number;
  eventListeners: number;
}

interface PerformanceThresholds {
  renderTime: number; // ms
  interactionTime: number; // ms
  memoryLimit: number; // MB
  maxDomNodes: number;
  maxEventListeners: number;
}

const DEFAULT_THRESHOLDS: PerformanceThresholds = {
  renderTime: 100, // 100ms
  interactionTime: 50, // 50ms
  memoryLimit: 50, // 50MB
  maxDomNodes: 1000,
  maxEventListeners: 100
};

export class PerformanceManager {
  private static instance: PerformanceManager;
  private thresholds: PerformanceThresholds;
  private metrics: PerformanceMetrics[] = [];
  private observers: PerformanceObserver[] = [];
  private rafCallbacks: Set<() => void> = new Set();
  private debouncedCallbacks: Map<string, number> = new Map();
  private throttledCallbacks: Map<string, number> = new Map();
  private intersectionObserver?: IntersectionObserver;
  private mutationObserver?: MutationObserver;

  constructor() {
    this.thresholds = { ...DEFAULT_THRESHOLDS };
    this.setupPerformanceMonitoring();
    this.setupOptimizations();
  }

  static getInstance(): PerformanceManager {
    if (!PerformanceManager.instance) {
      PerformanceManager.instance = new PerformanceManager();
    }
    return PerformanceManager.instance;
  }

  private setupPerformanceMonitoring(): void {
    // Performance Observer for measuring
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          this.processPerformanceEntries(entries);
        });
        
        observer.observe({ 
          entryTypes: ['measure', 'navigation', 'paint', 'largest-contentful-paint'] 
        });
        this.observers.push(observer);
      } catch (error) {
        console.warn('PerformanceObserver not fully supported:', error);
      }
    }

    // Memory monitoring
    this.startMemoryMonitoring();
  }

  private setupOptimizations(): void {
    // Setup virtual scrolling helpers
    this.setupIntersectionObserver();
    
    // Setup DOM mutation monitoring
    this.setupMutationObserver();
    
    // Optimize animations
    this.optimizeAnimations();
  }

  private setupIntersectionObserver(): void {
    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const target = entry.target as HTMLElement;
          
          if (entry.isIntersecting) {
            // Element is visible - enable expensive operations
            target.classList.add('visible');
            this.enableExpensiveOperations(target);
          } else {
            // Element is not visible - disable expensive operations
            target.classList.remove('visible');
            this.disableExpensiveOperations(target);
          }
        });
      }, {
        rootMargin: '50px',
        threshold: 0.1
      });
    }
  }

  private setupMutationObserver(): void {
    if ('MutationObserver' in window) {
      this.mutationObserver = new MutationObserver((mutations) => {
        let significant = false;
        
        mutations.forEach(mutation => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 10) {
            significant = true;
          }
        });
        
        if (significant) {
          this.scheduleOptimization();
        }
      });
      
      this.mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: false
      });
    }
  }

  private enableExpensiveOperations(element: HTMLElement): void {
    // Re-enable animations
    element.style.animationPlayState = 'running';
    
    // Re-enable complex event handlers
    const suspendedHandlers = element.dataset.suspendedHandlers;
    if (suspendedHandlers) {
      // Restore handlers if needed
      delete element.dataset.suspendedHandlers;
    }
  }

  private disableExpensiveOperations(element: HTMLElement): void {
    // Pause animations
    element.style.animationPlayState = 'paused';
    
    // Suspend complex event handlers for non-visible elements
    // Store handler info if needed
    element.dataset.suspendedHandlers = 'true';
  }

  private optimizeAnimations(): void {
    // Reduce animation complexity when performance is poor
    document.addEventListener('animationstart', (event) => {
      const target = event.target as HTMLElement;
      
      if (this.isPerformancePoor()) {
        target.style.willChange = 'auto';
        target.style.transform = 'translateZ(0)'; // Force GPU layer
      }
    });
  }

  private processPerformanceEntries(entries: PerformanceEntry[]): void {
    entries.forEach(entry => {
      switch (entry.entryType) {
        case 'measure':
          this.processMeasureEntry(entry as PerformanceMeasure);
          break;
        case 'paint':
          this.processPaintEntry(entry as PerformancePaintTiming);
          break;
        case 'largest-contentful-paint':
          this.processLCPEntry(entry);
          break;
      }
    });
  }

  private processMeasureEntry(entry: PerformanceMeasure): void {
    if (entry.name.startsWith('render-')) {
      if (entry.duration > this.thresholds.renderTime) {
        console.warn(`Slow render detected: ${entry.name} took ${entry.duration}ms`);
        this.scheduleOptimization();
      }
    }
  }

  private processPaintEntry(entry: PerformancePaintTiming): void {
    if (entry.name === 'first-contentful-paint' && entry.startTime > 3000) {
      console.warn(`Slow FCP: ${entry.startTime}ms`);
    }
  }

  private processLCPEntry(entry: PerformanceEntry): void {
    if (entry.startTime > 4000) {
      console.warn(`Poor LCP: ${entry.startTime}ms`);
    }
  }

  private startMemoryMonitoring(): void {
    if ('memory' in performance) {
      setInterval(() => {
        const memInfo = (performance as Performance & { memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number } }).memory;
        if (memInfo) {
          const usedMB = memInfo.usedJSHeapSize / 1048576;

          if (usedMB > this.thresholds.memoryLimit) {
            console.warn(`High memory usage: ${usedMB.toFixed(2)}MB`);
            this.performMemoryCleanup();
          }
        }
      }, 30000); // Check every 30 seconds
    }
  }

  private performMemoryCleanup(): void {
    // Clear unused event listeners
    this.cleanupEventListeners();
    
    // Clear old metrics
    if (this.metrics.length > 100) {
      this.metrics = this.metrics.slice(-50); // Keep last 50
    }
    
    // Clear caches if available
    this.clearCaches();
    
    // Suggest garbage collection
    if ('gc' in window && typeof (window as unknown as { gc?: () => void }).gc === 'function') {
      (window as unknown as { gc: () => void }).gc();
    }
  }

  private cleanupEventListeners(): void {
    // Remove duplicate or unused event listeners
    const elements = document.querySelectorAll('*');
    let totalListeners = 0;
    
    elements.forEach(element => {
      const listeners = (element as Element & { eventListeners?: unknown[] }).eventListeners || [];
      totalListeners += listeners.length;
    });
    
    if (totalListeners > this.thresholds.maxEventListeners) {
      console.warn(`Too many event listeners: ${totalListeners}`);
    }
  }

  private clearCaches(): void {
    // Clear browser caches when available
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          if (name.includes('temp') || name.includes('cache')) {
            caches.delete(name);
          }
        });
      });
    }
  }

  private scheduleOptimization(): void {
    // Use requestIdleCallback when available
    if ('requestIdleCallback' in window) {
      (window as unknown as { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(() => {
        this.optimizeDOM();
      });
    } else {
      setTimeout(() => {
        this.optimizeDOM();
      }, 0);
    }
  }

  private optimizeDOM(): void {
    const nodeCount = document.querySelectorAll('*').length;
    
    if (nodeCount > this.thresholds.maxDomNodes) {
      console.warn(`Large DOM detected: ${nodeCount} nodes`);
      this.virtualizeContent();
    }
  }

  private virtualizeContent(): void {
    // Implement simple virtualization for large lists
    const largeContainers = document.querySelectorAll('[data-virtualize]');
    
    largeContainers.forEach(container => {
      const items = container.children;
      if (items.length > 50) {
        this.setupVirtualScrolling(container as HTMLElement);
      }
    });
  }

  private setupVirtualScrolling(container: HTMLElement): void {
    const items = Array.from(container.children);
    const itemHeight = 40; // Estimate
    const viewportHeight = container.clientHeight;
    const visibleCount = Math.ceil(viewportHeight / itemHeight) + 2; // Buffer

    let scrollTop = 0;

    const updateVisible = () => {
      const startIndex = Math.floor(scrollTop / itemHeight);
      const endIndex = Math.min(startIndex + visibleCount, items.length);

      items.forEach((item, index) => {
        const element = item as HTMLElement;
        if (index >= startIndex && index < endIndex) {
          element.style.display = '';
          element.style.transform = `translateY(${index * itemHeight}px)`;
        } else {
          element.style.display = 'none';
        }
      });
    };

    container.addEventListener('scroll', this.throttle(() => {
      scrollTop = container.scrollTop;
      updateVisible();
    }, 16)); // ~60fps

    updateVisible();
  }

  private isPerformancePoor(): boolean {
    if (this.metrics.length < 5) return false;
    
    const recent = this.metrics.slice(-5);
    const avgRenderTime = recent.reduce((sum, m) => sum + m.renderTime, 0) / recent.length;
    
    return avgRenderTime > this.thresholds.renderTime;
  }

  // Public API methods
  measure(name: string, fn: () => void): void {
    const start = performance.now();
    performance.mark(`${name}-start`);
    
    fn();
    
    performance.mark(`${name}-end`);
    performance.measure(name, `${name}-start`, `${name}-end`);
    
    const duration = performance.now() - start;
    debugLog(`Performance: ${name} took ${duration.toFixed(2)}ms`);
  }

  async measureAsync<T>(name: string, fn: () => Promise<T>): Promise<T> {
    const start = performance.now();
    performance.mark(`${name}-start`);

    const result = await fn();

    performance.mark(`${name}-end`);
    performance.measure(name, `${name}-start`, `${name}-end`);

    const duration = performance.now() - start;
    debugLog(`Performance: ${name} took ${duration.toFixed(2)}ms`);

    return result;
  }

  debounce<T extends (...args: never[]) => void>(
    func: T,
    delay: number,
    key: string
  ): (...args: Parameters<T>) => void {
    return (...args: Parameters<T>) => {
      const existing = this.debouncedCallbacks.get(key);
      if (existing) {
        window.clearTimeout(existing);
      }

      const timeoutId = window.setTimeout(() => {
        func.apply(this, args);
        this.debouncedCallbacks.delete(key);
      }, delay);

      this.debouncedCallbacks.set(key, timeoutId);
    };
  }

  throttle<T extends (...args: never[]) => void>(
    func: T, 
    delay: number
  ): (...args: Parameters<T>) => void {
    let lastCall = 0;
    
    return (...args: Parameters<T>) => {
      const now = Date.now();
      if (now - lastCall >= delay) {
        lastCall = now;
        func.apply(this, args);
      }
    };
  }

  requestAnimationFrame(callback: () => void): void {
    this.rafCallbacks.add(callback);
    
    if (this.rafCallbacks.size === 1) {
      const processCallbacks = () => {
        const callbacks = Array.from(this.rafCallbacks);
        this.rafCallbacks.clear();
        
        callbacks.forEach(cb => {
          try {
            cb();
          } catch (error) {
            console.error('RAF callback error:', error);
          }
        });
        
        if (this.rafCallbacks.size > 0) {
          requestAnimationFrame(processCallbacks);
        }
      };
      
      requestAnimationFrame(processCallbacks);
    }
  }

  observeElement(element: HTMLElement): void {
    if (this.intersectionObserver) {
      this.intersectionObserver.observe(element);
    }
  }

  unobserveElement(element: HTMLElement): void {
    if (this.intersectionObserver) {
      this.intersectionObserver.unobserve(element);
    }
  }

  // Resource hints
  preloadResource(url: string, type: 'script' | 'style' | 'image' | 'font'): void {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = url;
    link.as = type;
    
    if (type === 'font') {
      link.crossOrigin = 'anonymous';
    }
    
    document.head.appendChild(link);
  }

  prefetchResource(url: string): void {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    document.head.appendChild(link);
  }

  // Performance report
  getPerformanceReport(): {
    metrics: PerformanceMetrics[];
    thresholds: PerformanceThresholds;
    recommendations: string[];
  } {
    const recommendations: string[] = [];
    
    if (this.isPerformancePoor()) {
      recommendations.push('Consider reducing animation complexity');
      recommendations.push('Optimize large DOM operations');
    }
    
    const nodeCount = document.querySelectorAll('*').length;
    if (nodeCount > this.thresholds.maxDomNodes) {
      recommendations.push('Implement virtualization for large lists');
    }
    
    if ('memory' in performance) {
      const memInfo = (performance as Performance & { memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number } }).memory;
      const usedMB = memInfo ? memInfo.usedJSHeapSize / 1048576 : 0;
      if (usedMB > this.thresholds.memoryLimit) {
        recommendations.push('Reduce memory usage');
      }
    }
    
    return {
      metrics: this.metrics,
      thresholds: this.thresholds,
      recommendations
    };
  }

  // Cleanup
  destroy(): void {
    this.observers.forEach(observer => observer.disconnect());
    this.intersectionObserver?.disconnect();
    this.mutationObserver?.disconnect();
    
    this.debouncedCallbacks.forEach(timeoutId => clearTimeout(timeoutId));
    this.debouncedCallbacks.clear();
    
    this.rafCallbacks.clear();
  }
}